import { useCallback, useLayoutEffect, useState } from 'react';

/**
 * 弹层壳（`DetailDrawer` / `FormModal`）「后打开的在上面」修正器。
 *
 * 非显然处：Arco 的 `Drawer`/`Modal` 容器（`.arco-drawer-wrapper`/`.arco-modal-wrapper`）只在**首次**
 * 打开时插入 `document.body`、此后位置不再变化，而两类容器的 z-index 同为弹层基准 +1
 * （`@z-index-drawer` = `@z-index-modal`），同层遮盖关系因此只由「容器在 body 中的先后」决定——
 * 先开过详情、再打开外层列表抽屉时，抽屉容器插入更晚，会把再次打开的详情盖住。
 *
 * 用法：把返回的 ref 回调挂到弹层**内部**任意节点上（壳里用 `display: contents` 中转节点，不生成盒子），
 * 打开时把它所属的 Arco 容器移到 body 末尾；不改任何 z-index，故不影响 `Modal.confirm`、Message 的既有层级。
 * 弹层被挂到非 body 容器（调用方自定义 `getPopupContainer`）时不动，尊重调用方的层级安排。
 */
const POPUP_WRAPPER_SELECTOR = '.arco-drawer-wrapper, .arco-modal-wrapper';

export default function usePopupTopLayer(visible: boolean) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const attach = useCallback((n: HTMLElement | null) => setNode(n), []);
  useLayoutEffect(() => {
    if (!visible || !node) return;
    const wrapper = node.closest(POPUP_WRAPPER_SELECTOR);
    if (!wrapper || wrapper.parentElement !== document.body) return;
    // 已在末尾就不动：重复 append 会重新插入节点，打断正在播的入场动画
    if (wrapper !== document.body.lastElementChild) document.body.appendChild(wrapper);
  }, [visible, node]);
  return attach;
}
